<template>
    <div
        v-if="visible"
        class="absolute w-screen h-screen top-0 left-0 bg-black/30 z-60 flex items-center justify-center cursor-pointer"
        @click.self.exact="hide"
    >
        <div
            id="conta-command"
            class="rounded bg-base-200 shadow-lg cursor-auto w-[64em] px-2 pb-2"
            @keydown.left.stop.prevent="keyLeft"
            @keydown.right.stop.prevent="keyRight"
        >
            <div class="flex gap-2 pb-1">
                <i class="i-ph-terminal mt-3 text-primary text-xl"/>
                <img src="/conta-logo-light.svg" class="w-24" alt="conta logo">
                <div class="mt-2 text-lg italic">Command</div>
                <div class="grow"/>
            </div>
            <UInput
                id="commandbar_input"
                v-model="search"
                size="lg"
                class="w-full"
                placeholder="Søk på foretak, kunder, leverandører, navigasjon og mye mer..."
                autofocus
                @update:model-value="onSearchChange"
                @keydown.up.exact="handleUpArrow"
                @keydown.down.exact="handleDownArrow"
                @keydown.enter.exact="selectHighlightedOption"
            />
            <RecycleScroller
                ref="recycleScroller"
                v-slot="{item}"
                :items="items"
                key-field="key"
                :item-size="44"
                class="h-72 w-full mt-2 bg-base-100"
                @keydown.up.exact="handleUpArrow"
                @keydown.down.exact="handleDownArrow"
                @keydown.enter.exact="selectHighlightedOption"
            >
                <div
                    :id="`commandbar-option-${item.key}`"
                    :key="item.key"
                    class="flex h-[44px] gap-1 items-center px-2 cursor-pointer"
                    :class="item.index === highlightedIndex ? 'bg-primary/20': ''"
                    @click="optionSelected(item)"
                    @mousemove="highlightedIndex = item.index"
                >
                    <div class="command-option-type">{{ item.typeLabel }}</div>
                    <i class="i-ph-caret-right"/>
                    <img
                        v-if="item.imageSrc"
                        :src="item.imageSrc"
                        class="w-6"
                        alt="option icon"
                    >
                    <i v-else :class="item.icon" class="w-6 text-primary"/>
                    <div class="grow truncate flex" :class="item.isActive() ? 'font-bold' : ''">
                        <template v-if="typeof item.label === 'function'">
                            {{ item.label(search) }}
                        </template>
                        <template v-else>
                            {{ item.label }}
                        </template>
                        <div v-if="item.index === highlightedIndex && item.hotkey" class="ml-2">
                            <UKbd>Shift+Shift</UKbd>
                            <UKbd>{{ item.hotkey }}</UKbd>
                            <UKbd>1-4</UKbd>
                        </div>
                    </div>
                    <multi-panel-target-indicator v-if="item.index === highlightedIndex && item.showPanelSelector" v-model="targetPanelIndex"/>
                    <component :is="item.rightContent"/>
                    <div class="w-12 flex justify-end">
                        <i v-if="item.isActive()" class="text-xs i-ph-check"/>
                    </div>
                </div>
            </RecycleScroller>
        </div>
    </div>
</template>

<script setup lang="ts">
import {computed, onMounted, onUnmounted, ref} from 'vue'
// @ts-ignore
import {RecycleScroller} from 'vue-virtual-scroller'
import {HTMLInputElement} from 'happy-dom'
import {CommandBarOption, useContaCommand} from './commandBar.service'
import {useEmitter} from '@/services/infrastructure/emitter'
import { MultiPanelTargetIndicator } from '@tritt/multipanel'

const {search, filteredOptions, targetPanelIndex, isHotkeyMatchWithPanelTarget} = useContaCommand()
const {onEvent, cleanUp} = useEmitter()
const visible = ref(false)
const timeOfLastVisible = ref(0)

const TRIGGER_INTERVAL = 500
const timeOfLastShift = ref(0)
let prevKey = ''

let prevActiveElement: Element | null
const highlightedIndex = ref(0)

const items = computed(() => {
    return filteredOptions.value.map((option, index) => ({
        ...option,
        index
    }))
})

window.onkeyup = (event) => {
    if (event.key === 'Shift') {
        const now = Date.now()
        const timeSinceLastPress = now - timeOfLastShift.value

        if (timeSinceLastPress < TRIGGER_INTERVAL && prevKey === 'Shift') {
            prevKey = ''
            toggle()
        }

        timeOfLastShift.value = Date.now()
    }

    prevKey = event.key

    if (event.key === 'Escape') {
        hide()
    }
}

const onSearchChange = () => {
    highlightedIndex.value = 0
    ensureElementAtIndexIsVisible(highlightedIndex.value)
    const highlightedOption = filteredOptions.value[highlightedIndex.value]
    if (highlightedOption) {
        const targetPanelHit = isHotkeyMatchWithPanelTarget(highlightedOption, search.value)
        if (targetPanelHit && targetPanelHit >= 1 && targetPanelHit <= 4) {
            targetPanelIndex.value = targetPanelHit
        }
    }
}

const toggle = () => {
    if (!visible.value) {
        timeOfLastVisible.value = Date.now()
        show()
    } else {
        if (Date.now() - timeOfLastVisible.value > TRIGGER_INTERVAL) hide()
    }
}

const show = () => {
    highlightedIndex.value = 0
    prevActiveElement = document.activeElement
    visible.value = true
    search.value = ''
}

const hide = () => {
    highlightedIndex.value = 0
    visible.value = false
    search.value = ''

    // @ts-ignore
    if (prevActiveElement && prevActiveElement.focus) {
        const focusableElement = prevActiveElement as unknown as HTMLInputElement
        focusableElement.focus()
        prevActiveElement = null
    }
}

const ensureElementAtIndexIsVisible = (index: number) => {
    const item = filteredOptions.value[index]
    if (!item) {
        console.warn(`Attempting to make visible item at index ${index}, but it does not exist in filteredOptions`)
        return
    }
    const element = document.getElementById(`commandbar-option-${item.key}`)

    if (!element) return

    const scrollContainer = element.closest('.vue-recycle-scroller') || element.parentElement
    if (!scrollContainer) {
        element.scrollIntoView({block: 'nearest'})
        return
    }

    const elementRect = element.getBoundingClientRect()
    const containerRect = scrollContainer.getBoundingClientRect()

    // Check if element is visible within the scrollable area
    const isVisible = (
        elementRect.top >= containerRect.top &&
        elementRect.bottom <= containerRect.bottom
    )

    // Only scroll if not visible
    if (!isVisible) {
        element.scrollIntoView({block: 'nearest'})
    }

}

const handleUpArrow = () => {
    if (highlightedIndex.value > 0) {
        highlightedIndex.value--
        ensureElementAtIndexIsVisible(highlightedIndex.value)
    }
}

const handleDownArrow = () => {
    if (highlightedIndex.value < filteredOptions.value.length - 1) {
        highlightedIndex.value++
        ensureElementAtIndexIsVisible(highlightedIndex.value)
    }
}

const optionSelected = (option: CommandBarOption) => {
    const searchValue = search.value
    hide()
    option.activate(targetPanelIndex.value, searchValue)
}

const selectHighlightedOption = () => {
    const option = filteredOptions.value[highlightedIndex.value]
    if (option) {
        optionSelected(option)
    } else {
        console.warn(`No option found at highlighted index ${highlightedIndex.value}`)
    }
}

const keyLeft = () => {
    if (targetPanelIndex.value > 1) {
        targetPanelIndex.value--
    }
}

const keyRight = () => {
    if (targetPanelIndex.value < 4) {
        targetPanelIndex.value++
    }
}

onMounted(() => onEvent('SHOW_COMMAND_BAR', show))
onUnmounted(cleanUp)

</script>

<style src="vue-multiselect/dist/vue-multiselect.css"></style>

<style>

</style>